import { useRouter } from 'next/router';
import { useEffect, useState, useRef } from 'react';
import { useData } from 'hooks/useData';
import { useModal } from 'hooks/3catinfo/useModal';
import { useViewport } from 'hooks/3catinfo/useViewport';
import { addParameterToUrl, getDataUTC } from '@portal-internet/core';
import { getHoraAmbPunt } from 'utils/3catinfo/dateHelper';
import __isEmpty from 'lodash/isEmpty';
import clsx from 'clsx';
import Slider from 'components/3catinfo/Slider';
import Icona from 'components/3catinfo/ui/Icona';
import MomentClau from './MomentClau';
import Etiquetes from 'components/3catinfo/Etiquetes';
import Compartir from 'components/3catinfo/Compartir';
import Cos from 'components/3catinfo/ItemMinutaminut/Cos';
import styles from './momentsclau.module.scss';

const getSharedLink = (id, asPath) => {
    const queryParamShare = 'minut';
    return addParameterToUrl(asPath, queryParamShare, id);
};

const ModalContent = (props) => {
    const { items, onClickClose, onIndexChange, urlBase } = props;
    const [index, setIndex] = useState(props.index);
    const initialFocusRef = useRef(null);
    const modalScrollRef = useRef(null);
    const fletxaEsquerraRef = useRef(null);
    const fletxaDretaRef = useRef(null);
    const { data, getDades } = useData();

    const stylesPrevious = clsx({
        [styles.disabled]: index === 1
    });
    const stylesNext = clsx({
        [styles.disabled]: index === items.length
    });
    const momentClau = data?.[0];
    const { asPath } = useRouter();
    const { isMobile } = useViewport();
    const isBlanc = !isMobile ? true : false;
    const [cosVisited, setCosVisited] = useState(false);
    const [announcement, setAnnouncement] = useState('');

    useEffect(() => {
        if (initialFocusRef.current) {
            initialFocusRef.current.focus();
        }
    }, []);

    useEffect(() => {
        const idMomentClau = items[index - 1]?.id;
        if (idMomentClau) {
            const url = urlBase.replace('[id_moment_clau]', idMomentClau);
            getDades(url);
            onIndexChange(index);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [index]);

    const focusModalScroll = (event) => {
        if (modalScrollRef.current) {
            modalScrollRef.current.focus();
            setCosVisited(true);
        }
    };

    const saltaEnllacosCos = (event) => {
        if (cosVisited) {
            if (index === 1) {
                fletxaDretaRef.current.focus();
            } else {
                fletxaEsquerraRef.current.focus();
            }
            setCosVisited(false);
        }
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Tab' && event.shiftKey) {
            setCosVisited(false);
        }
    };
    const handleUpdate = (numMomentClau) => {
        setAnnouncement(`Nou contingut, moment clau ${numMomentClau}`);
        setTimeout(() => {
            setAnnouncement('');
        }, 1000);
    };

    return (
        <div>
            <div className={styles.modal_mask}>
                <div className={styles.modal_wrapper}>
                    <div className={styles.controls}>
                        <div className={styles.controlsInner}>
                            <button onClick={onClickClose} ref={initialFocusRef}>
                                <Icona className={styles.iconaTancar} icona='tanca' alt='tanca' mida='32' isBlanc={isBlanc} />
                            </button>
                        </div>
                    </div>
                    {momentClau && (
                        <>
                            <div className={styles.modal_content}>
                                <time className={styles.modal_time} dateTime={getDataUTC(momentClau?.data_publicacio)}>
                                    <span>{getHoraAmbPunt(momentClau?.hora)}</span>
                                </time>
                                <Compartir
                                    obreEsquerra={true}
                                    className={styles.modal_compartir}
                                    estil='small'
                                    route={{ href: getSharedLink(momentClau?.id, asPath) }}
                                />
                                <div
                                    className={styles.modal_scroll}
                                    ref={modalScrollRef}
                                    onBlur={saltaEnllacosCos}
                                    onKeyDown={handleKeyDown}
                                >
                                    <Etiquetes agrupacions={momentClau?.agrupacions} />
                                    <h2
                                        className={styles.modal_titol}
                                        dangerouslySetInnerHTML={{ __html: momentClau?.titol }}
                                    ></h2>
                                    <div className={styles.modal_text} onBlur={focusModalScroll}>
                                        <Cos entradeta={momentClau?.entradeta} estil='modal' />
                                    </div>
                                </div>
                            </div>
                            <div className={styles.modal_buttons}>
                                <button
                                    ref={fletxaEsquerraRef}
                                    className={stylesPrevious}
                                    tabIndex={index === 1 ? -1 : 0}
                                    onClick={() => {
                                        if (index > 1) {
                                            setIndex(index - 1);
                                            handleUpdate(index - 1);
                                        }
                                    }}
                                >
                                    <Icona
                                        className={styles.icona}
                                        icona='fletxa_esquerra'
                                        alt='Anterior moment clau'
                                        inline={true}
                                    />
                                </button>
                                <button
                                    ref={fletxaDretaRef}
                                    className={stylesNext}
                                    tabIndex={index === items.length ? -1 : 0}
                                    onClick={() => {
                                        if (index < items.length) {
                                            setIndex(index + 1);
                                            handleUpdate(index + 1);
                                        }
                                    }}
                                >
                                    <Icona
                                        className={styles.icona}
                                        icona='fletxa_dreta'
                                        alt='Següent moment clau'
                                        inline={true}
                                    />
                                </button>
                                <div className={styles.modal_pagecounter}>
                                    {index} / {items.length}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
            <div aria-live='assertive' className={styles.announcement}>
                {announcement}
            </div>
        </div>
    );
};

const MomentsClau = (props) => {
    const { items, finalitzat, urlBase } = props;
    const { setModal, removeModal } = useModal();
    const indexModalActive = useRef();
    const swiperRef = useRef();

    const handleCloseModal = () => {
        removeModal();
        swiperRef?.current?.slideTo(indexModalActive.current);
        const lastMomentFocus = document.querySelector(`[data-moment-clau="${indexModalActive.current}"]`);
        if (indexModalActive.current && lastMomentFocus) {
            setTimeout(() => {
                lastMomentFocus.focus();
            }, 100);
        }
    };

    const openModalContent = (index) => {
        setModal(
            <ModalContent
                index={index}
                items={items}
                urlBase={urlBase}
                onIndexChange={(index) => (indexModalActive.current = index)}
            />,
            {
                onClickClose: handleCloseModal,
                className: styles.modal,
                closeOnClickOutside: true,
                mode: 'full-screen'
            }
        );
    };

    return (
        items && (
            <>
                <div className={styles.moments_clau}>
                    <div className={styles.inner_moment_clau}>
                        <Slider
                            titol='Moments clau'
                            format='moments-clau'
                            titolClass='small'
                            estilModul='momentsClaus'
                            onSwiper={(swiper) => (swiperRef.current = swiper)}
                        >
                            {items?.map((item, i) => {
                                return (
                                    <MomentClau
                                        key={`moment-clau-${item.id}`}
                                        index={i + 1}
                                        onClick={() => openModalContent(i + 1, item.urlBase)}
                                        finalitzat={finalitzat}
                                        {...item}
                                    />
                                );
                            })}
                        </Slider>
                    </div>
                </div>
            </>
        )
    );
};

export default MomentsClau;
